import React, { useState } from 'react';
import { format } from 'date-fns';
import {
  PencilIcon,
  TrashIcon,
  CheckIcon,
  XMarkIcon,
  EyeIcon
} from '@heroicons/react/24/outline';
import DueDateLegend from './DueDateLegend';

const TaskList = ({ 
  tasks, 
  onDelete, 
  onEdit, 
  onToggleComplete,
  onView,
  users,
  departments,
  groups,
  types
}) => {
  // Dialog state for delete confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [deleteEntireSeries, setDeleteEntireSeries] = useState(false);
  const [deleteFutureTasks, setDeleteFutureTasks] = useState(false);
  
  // Parse dates from the server consistently
  const parseTaskDate = (dateString) => {
    if (!dateString) return null;
    
    try {
      // Split the date string to get components
      const parts = dateString.split('T')[0].split('-');
      if (parts.length !== 3) return new Date(dateString);
      
      // Create date with noon time to avoid timezone shifting
      const year = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1; // JS months are 0-indexed
      const day = parseInt(parts[2], 10);
      
      return new Date(year, month, day, 12, 0, 0);
    } catch (e) {
      console.warn('Error parsing task date:', e);
      return new Date(dateString);
    }
  };
  
  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    try {
      return format(parseTaskDate(dateString), 'MMM d, yyyy');
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid date';
    }
  };
  
  // Get due date status
  const getDueDateStatus = (dateString) => {
    if (!dateString) return { status: 'none', className: 'text-gray-400' };
    
    const dueDate = parseTaskDate(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const diffTime = dueDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
      return { status: 'overdue', className: 'text-red-500 font-medium' };
    } else if (diffDays <= 7) {
      return { status: 'soon', className: 'text-yellow-500 font-medium' };
    }
    
    return { status: 'future', className: 'text-green-500' };
  };
  
  // Function to get the color for a specific entity (department, group, type)
  const getEntityColor = (entityId, entityType) => {
    let entity;
    switch (entityType) {
      case 'department':
        entity = departments.find(d => d.id === entityId);
        break;
      case 'group':
        entity = groups.find(g => g.id === entityId);
        break;
      case 'type':
        entity = types.find(t => t.id === entityId);
        break;
      default:
        return '#6B7280'; // Default gray
    }
    
    return entity?.color || '#6B7280';
  };
  
  // Handle delete button click
  const handleDeleteClick = (task) => {
    setTaskToDelete(task);
    setDeleteEntireSeries(false);
    setDeleteFutureTasks(false);
    setDeleteDialogOpen(true);
  };
  
  // Execute the deletion
  const confirmDelete = () => {
    if (taskToDelete) {
      onDelete(taskToDelete.id, deleteEntireSeries, deleteFutureTasks);
      setDeleteDialogOpen(false);
      setTaskToDelete(null);
    }
  };
  
  // Cancel deletion
  const cancelDelete = () => {
    setDeleteDialogOpen(false);
    setTaskToDelete(null);
  };
  
  // Find user name by ID
  const getUserName = (userId) => {
    const user = users.find(u => u.id === userId);
    return user ? user.name : 'Unassigned';
  };
  
  return (
    <>
      {/* Due Date Legend */}
      <DueDateLegend viewType="list" />
      
      <div className="bg-gray-800 shadow overflow-hidden border border-gray-700 sm:rounded-lg">
        <table className="min-w-full divide-y divide-gray-700">
          <thead className="bg-gray-700">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                Task
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                Assigned To
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                Due Date
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                Category
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                Status
              </th>
              <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-300 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-gray-800 divide-y divide-gray-700">
            {tasks.length > 0 ? (
              tasks.map((task) => {
                const dueDateStatus = getDueDateStatus(task.due_date);
                const departmentColor = getEntityColor(task.department_id, 'department');
                const groupColor = getEntityColor(task.group_id, 'group');
                const typeColor = getEntityColor(task.type_id, 'type');
                
                return (
                  <tr key={task.id} className={`${task.is_completed ? 'bg-gray-900/50' : ''} hover:bg-gray-700`}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="ml-4">
                          <div className={`text-sm font-medium ${task.is_completed ? 'text-gray-400 line-through' : 'text-white'}`}>
                            {task.name}
                          </div>
                          <div className="text-sm text-gray-400 max-w-xs truncate">
                            {task.description || 'No description'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-300">
                        {task.assigned_user_id 
                          ? getUserName(task.assigned_user_id)
                          : 'Unassigned'
                        }
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className={`text-sm ${dueDateStatus.className}`}>
                        {formatDate(task.due_date)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex space-x-2">
                        {task.department_id && (
                          <span 
                            className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full"
                            style={{ 
                              backgroundColor: `${departmentColor}40`, // 40 is for 25% opacity
                              color: departmentColor,
                              border: `1px solid ${departmentColor}`
                            }}
                          >
                            {task.department_name || 'Department'}
                          </span>
                        )}
                        {task.group_id && (
                          <span 
                            className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full"
                            style={{ 
                              backgroundColor: `${groupColor}40`,
                              color: groupColor,
                              border: `1px solid ${groupColor}`
                            }}
                          >
                            {task.group_name || 'Group'}
                          </span>
                        )}
                        {task.type_id && (
                          <span 
                            className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full"
                            style={{ 
                              backgroundColor: `${typeColor}40`,
                              color: typeColor,
                              border: `1px solid ${typeColor}`
                            }}
                          >
                            {task.type_name || 'Type'}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span 
                        className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          task.is_completed 
                            ? 'bg-green-900/25 text-green-400 border border-green-700' 
                            : 'bg-blue-900/25 text-blue-400 border border-blue-700'
                        }`}
                      >
                        {task.is_completed ? 'Completed' : 'Active'}
                      </span>
                      {task.is_recurring && (
                        <span className="ml-2 px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-purple-900/25 text-purple-400 border border-purple-700">
                          {task.recurrence_pattern}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() => onToggleComplete(task.id, !task.is_completed)}
                          className={`p-1 rounded-full ${
                            task.is_completed 
                              ? 'text-gray-400 hover:text-gray-300 hover:bg-gray-600' 
                              : 'text-green-400 hover:text-green-300 hover:bg-green-900/25'
                          }`}
                          title={task.is_completed ? 'Mark as incomplete' : 'Mark as complete'}
                        >
                          {task.is_completed ? (
                            <XMarkIcon className="h-5 w-5" />
                          ) : (
                            <CheckIcon className="h-5 w-5" />
                          )}
                        </button>
                        
                        <button
                          onClick={() => onView(task)}
                          className="p-1 rounded-full text-gray-400 hover:text-gray-300 hover:bg-gray-600"
                          title="View task details"
                        >
                          <EyeIcon className="h-5 w-5" />
                        </button>
                        
                        <button
                          onClick={() => onEdit(task)}
                          className="p-1 rounded-full text-blue-400 hover:text-blue-300 hover:bg-blue-900/25"
                          title="Edit task"
                        >
                          <PencilIcon className="h-5 w-5" />
                        </button>
                        
                        <button
                          onClick={() => handleDeleteClick(task)}
                          className="p-1 rounded-full text-red-400 hover:text-red-300 hover:bg-red-900/25"
                          title="Delete task"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="6" className="px-6 py-10 text-center text-gray-400">
                  No tasks found. Click on "Add Task" to create one.
                </td>
              </tr>
            )}
          </tbody>
        </table>
        
        {/* Delete Confirmation Dialog */}
        {deleteDialogOpen && taskToDelete && (
          <div className="fixed z-10 inset-0 overflow-y-auto">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                <div className="absolute inset-0 bg-gray-900 opacity-75"></div>
              </div>
              
              <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
              
              <div 
                className="inline-block align-bottom bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full border border-gray-700"
                role="dialog" 
                aria-modal="true" 
                aria-labelledby="modal-headline"
              >
                <div className="bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-900/50 sm:mx-0 sm:h-10 sm:w-10">
                      <TrashIcon className="h-6 w-6 text-red-400" aria-hidden="true" />
                    </div>
                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                      <h3 className="text-lg leading-6 font-medium text-gray-200" id="modal-headline">
                        Delete Task
                      </h3>
                      <div className="mt-2">
                        <p className="text-sm text-gray-300">
                          Are you sure you want to delete the task "{taskToDelete.name}"? This action cannot be undone.
                        </p>
                        
                        {taskToDelete.is_recurring && taskToDelete.recurring_series_id && (
                          <div className="mt-4 space-y-2">
                            <p className="text-sm font-medium text-gray-300">
                              This is a recurring task. What would you like to delete?
                            </p>
                            
                            <label className="flex items-center">
                              <input
                                type="radio"
                                className="form-radio h-4 w-4 text-indigo-600 rounded-full bg-gray-700 border-gray-600"
                                name="delete-option"
                                checked={!deleteEntireSeries && !deleteFutureTasks}
                                onChange={() => {
                                  setDeleteEntireSeries(false);
                                  setDeleteFutureTasks(false);
                                }}
                              />
                              <span className="ml-2 text-sm text-gray-300">
                                Only this task
                              </span>
                            </label>
                            
                            <label className="flex items-center">
                              <input
                                type="radio"
                                className="form-radio h-4 w-4 text-indigo-600 rounded-full bg-gray-700 border-gray-600"
                                name="delete-option"
                                checked={deleteFutureTasks}
                                onChange={() => {
                                  setDeleteEntireSeries(false);
                                  setDeleteFutureTasks(true);
                                }}
                              />
                              <span className="ml-2 text-sm text-gray-300">
                                This task and all future tasks in this series
                              </span>
                            </label>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-900/50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-800 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                    onClick={confirmDelete}
                  >
                    Delete
                  </button>
                  <button
                    type="button"
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-600 shadow-sm px-4 py-2 bg-gray-700 text-base font-medium text-gray-200 hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                    onClick={cancelDelete}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default TaskList; 